import { useCallback, useContext, useMemo, useState } from 'react';
import { LOG_ACTIONS, useLogAmplitude } from '@namespace/analytics';
import { useGeneralRoutes } from '@namespace/cms';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { useHistory } from '@namespace/router';
import { UserContext } from '@namespace/user';
import { accept } from '../../api/acceptBonus';
import { bonusTab } from '../../constants';

export const useRiskFreeBonusActions = (bonus, onAccept) => {
  const [isRequestProcessing, setIsRequestProcessing] = useState(false);
  const { bonusId, currency, isActivation, isActive, bonusModelId } = bonus;
  const { t, language, timezone, f } = useI18n();
  const { push } = useHistory();
  const { errorNotification, successNotification } = useNotifications();
  const { riskFreeBonusDetails, liveSportAll } = useGeneralRoutes();

  const DATE_FORMATS_TYPES = f.getFormats();
  const [user] = useContext(UserContext);
  const { registrationDate } = user;
  const logAmplitude = useLogAmplitude();

  const analyticParams = useMemo(() => {
    const formattedRegistrationDate = f
      .getDateTime({ date: registrationDate?.replace('Z', '') })
      .toFormat(DATE_FORMATS_TYPES.API_WITH_TIME);

    return {
      bonus_option: 'riskfree',
      bonus_template_id: bonusModelId,
      bonus_source: 'crm',
      registration_date: formattedRegistrationDate
    };
  }, [DATE_FORMATS_TYPES.API_WITH_TIME, bonusModelId, f, registrationDate]);

  const handleClick = useCallback(
    async (event) => {
      event.stopPropagation();
      if (isActive) {
        push(liveSportAll);
      } else {
        setIsRequestProcessing(true);
        try {
          const message = await accept(
            {
              bonus_id: bonusId,
              lang: language,
              time_zone: timezone
            },
            bonusTab.riskFree
          );
          setIsRequestProcessing(false);
          successNotification(t(message));
          if (onAccept) {
            onAccept();
          }
          logAmplitude({
            actionName: LOG_ACTIONS.BONUS_ACCEPT_CLICK,
            attributes: analyticParams
          });
        } catch (e) {
          setIsRequestProcessing(false);
          errorNotification(
            t(e.message || 'accountingMessages.accounting_error_6', [currency])
          );
        }
      }
    },
    [
      isActive,
      push,
      liveSportAll,
      bonusId,
      language,
      timezone,
      successNotification,
      t,
      onAccept,
      logAmplitude,
      analyticParams,
      errorNotification,
      currency
    ]
  );

  const openDetails = useCallback(
    () =>
      !isRequestProcessing || !isActivation
        ? push(`${riskFreeBonusDetails}/${bonusId}`)
        : null,
    [isRequestProcessing, isActivation, push, riskFreeBonusDetails, bonusId]
  );

  return {
    handleClick,
    openDetails,
    isRequestProcessing
  };
};
